import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { EventType, RedirectAction, Route, RouteParameters, SearchParameters } from '../../constants'
import { useMixpanel, useSearchParameters } from '../../hooks'
import { appendSearchParamsToPathname, hydratePathname, isCountrySupported, omit } from '../../util'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import styled from '@emotion/styled'

const initialMessage = 'pages.redirect.genericError'

const Container = styled('div')`
  padding: 5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
`

const Director = () => {
  const navigate = useNavigate()
  const { mixpanel } = useMixpanel()
  const { pathname: originalPathname, search } = useLocation()
  const [message, setMessage] = useState(initialMessage)
  const { t } = useTranslation()
  const searchParams = omit(Object.fromEntries(new URLSearchParams(search)), ['action'])

  const {
    [SearchParameters.ACTION]: action,
    [SearchParameters.ID]: appointmentId,
    [SearchParameters.RESOURCE_ID]: resourceId,
    [SearchParameters.SHOP_ID]: shopId,
    [SearchParameters.COUNTRY]: country,
  } = useSearchParameters()

  const [isMounted, beMounted] = useState(false)

  const isValidCountry = useMemo(() => isCountrySupported(country as string), [])

  useEffect(() => {
    if (country && !isValidCountry && mixpanel) {
      const url = `${originalPathname}${search}`
      mixpanel.track(EventType.ERROR_INVALID_COUNTRY, { url })
      console.error(`[DirectorInvalidCountry] Invalid country ${country}, url: ${url}`)
    }

    if (!country && mixpanel) {
      mixpanel.track(EventType.ERROR_NO_COUNTRY)
    }
  }, [mixpanel])

  useEffect(() => {
    let pathname = ''
    let pathnameAndSearch = ''

    if (isValidCountry) {
      const routeParams = { [RouteParameters.COUNTRY]: country }
      switch (true) {
        case action === RedirectAction.CANCEL && Boolean(appointmentId):
          pathname = hydratePathname(Route.APPOINTMENT_CANCELLATION, {
            ...routeParams,
            [RouteParameters.APPOINTMENT_ID]: appointmentId,
          })

          navigate(pathname, { replace: true })

          break
        case action === RedirectAction.RESCHEDULE && Boolean(appointmentId):
          pathname = hydratePathname(Route.APPOINTMENT_RESCHEDULE, {
            ...routeParams,
            [RouteParameters.APPOINTMENT_ID]: appointmentId,
          })
          navigate(pathname, { replace: true })

          break
        case action === RedirectAction.SCHEDULE:
          pathname = hydratePathname(Route.CONSULTANT_SCHEDULE, routeParams)
          pathnameAndSearch = appendSearchParamsToPathname({ pathname, searchParams })
          navigate(pathnameAndSearch, { replace: true })

          break
        case action === RedirectAction.SETTINGS_RESOURCE_BLOCK_SCHEDULING && Boolean(resourceId):
          pathname = hydratePathname(Route.SETTINGS_RESOURCE_BLOCK_SCHEDULING, {
            ...routeParams,
            [RouteParameters.RESOURCE_ID]: resourceId,
          })

          navigate(pathname, { replace: true })

          break
        case action === RedirectAction.SHOP_RESOURCES_BLOCK_SCHEDULING && Boolean(shopId) && Boolean(resourceId):
          pathname = hydratePathname(Route.SHOP_SELECTED_RESOURCE_BLOCK_SCHEDULING, {
            ...routeParams,
            [RouteParameters.SHOP_ID]: shopId,
            [RouteParameters.RESOURCE_ID]: resourceId,
          })
          navigate(pathname, { replace: true })

          break
        case action === RedirectAction.SHOP_RESOURCES_BLOCK_SCHEDULING && Boolean(shopId) && !Boolean(resourceId):
          pathname = hydratePathname(Route.SHOP_RESOURCES_BLOCK_SCHEDULING, {
            ...routeParams,
            [RouteParameters.SHOP_ID]: shopId,
          })
          navigate(pathname, { replace: true })

          break
        case action === RedirectAction.SETTINGS_RESOURCE_AVAILABILITY_CALENDAR && Boolean(resourceId):
          pathname = hydratePathname(Route.SETTINGS_RESOURCE_AVAILABILITY_CALENDAR, {
            ...routeParams,
            [RouteParameters.RESOURCE_ID]: resourceId,
          })
          pathnameAndSearch = appendSearchParamsToPathname({ pathname, searchParams })
          navigate(pathnameAndSearch, { replace: true })

          break
        case action === RedirectAction.RESERVATION:
          pathname = hydratePathname(Route.RESERVATION, routeParams)
          pathnameAndSearch = appendSearchParamsToPathname({ pathname, searchParams })
          navigate(pathnameAndSearch, { replace: true })

          break
        default:
          // Action not supported or missing search params
          console.error('Action or action info not valid', {
            ...searchParams,
            action,
          })
          return
      }
    } else {
      setMessage(country ? 'pages.redirect.invalidCountry' : 'pages.redirect.noCountry')
    }
  }, [])

  useEffect(() => beMounted(true), [])

  if (!isMounted) {
    return null
  }

  return (
    <Container data-testid="error-message">
      <ReportProblemIcon /> {t(message, { country })}
    </Container>
  )
}

export { Director }
